/**
 * ContactForm Component
 *
 * Form for updating contact information.
 */

import { useState } from 'react';
import { Mail, Phone, Globe, MapPin } from 'lucide-react';
import { Button } from '@elbklar/ui';
import { TextField } from '@/components/form/Felder';
import { useUpdateContact } from '@/hooks/useTenantConfig';

interface TenantConfig {
  email: string | null;
  phone: string | null;
  website: string | null;
  street: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
}

interface ContactFormProps {
  config: TenantConfig;
}

export function ContactForm({ config }: ContactFormProps) {
  const [email, setEmail] = useState(config.email || '');
  const [phone, setPhone] = useState(config.phone || '');
  const [website, setWebsite] = useState(config.website || '');
  const [street, setStreet] = useState(config.street || '');
  const [postalCode, setPostalCode] = useState(config.postal_code || '');
  const [city, setCity] = useState(config.city || '');
  const [country, setCountry] = useState(config.country || 'Deutschland');

  const updateMutation = useUpdateContact();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await updateMutation.mutateAsync({
      email: email || undefined,
      phone: phone || undefined,
      website: website || undefined,
      street: street || undefined,
      postal_code: postalCode || undefined,
      city: city || undefined,
      country: country || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
      {/* Email */}
      <TextField label="E-Mail" icon={Mail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="info@elbklar.com" />

      {/* Phone */}
      <TextField label="Telefon" icon={Phone} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+49 351 123 456" />

      {/* Website */}
      <TextField label="Website" icon={Globe} type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://elbklar.com" />

      {/* Address Section */}
      <div className="border-t border-border pt-4">
        <h3 className="mb-4 flex items-center gap-2 t-title">
          <MapPin size={18} strokeWidth={1.75} className="text-highlight" aria-hidden />
          Adresse
        </h3>

        <div className="space-y-4">
          {/* Street */}
          <TextField label="Straße" type="text" value={street} onChange={(e) => setStreet(e.target.value)} placeholder="Weinbergstraße 5" />

          {/* Postal Code + City */}
          <div className="grid grid-cols-3 gap-4">
            <TextField label="PLZ" type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="01640" />
            <TextField label="Ort" className="col-span-2" type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Coswig" />
          </div>

          {/* Country */}
          <TextField label="Land" type="text" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Deutschland" />
        </div>
      </div>

      {/* Submit */}
      <div className="flex items-center gap-4 border-t border-border pt-4">
        <Button type="submit" variant="primary" loading={updateMutation.isPending}>
          Speichern
        </Button>
        {updateMutation.isSuccess && <span className="t-small text-success">Gespeichert</span>}
        {updateMutation.isError && (
          <span className="t-small text-danger">{(updateMutation.error as Error)?.message || 'Speichern fehlgeschlagen'}</span>
        )}
      </div>
    </form>
  );
}

export default ContactForm;
